import React, { useRef, useState } from "react"
import styled from "@emotion/styled"
import Section from "src/components/Section"
import Grid from "src/components/Grid"
import Media from "src/components/Media"
import TextLockup from "src/components/TextLockup"
import Slider from "react-slick"
import ResponsiveComponent from "src/components/ResponsiveComponent"
import { colors, typography, animations, mq, globals, util } from "src/styles"
import { Card, shadow } from "src/styles/globals"

const Wrapper = styled(Section)`
  text-align: center;
  h1,
  h2,
  h3,
  h4,
  h5,
  h6,
  p,
  blockquote,
  ol,
  ul,
  dl {
    margin-left: auto;
    margin-right: auto;
  }
  h1,
  h2,
  h3 {
    max-width: 20em;
  }
  h4,
  h5 {
    max-width: 26em;
  }
  h6 {
    max-width: 16em;
  }
  p {
    max-width: 36em;
  }
  ${({ vertical }) =>
    `
    ${globals.verticalSpacing("padding-top", vertical)}
    ${globals.verticalSpacing("padding-bottom", vertical)}
  `}
  ${({ horizontal }) =>
    `
    ${globals.verticalSpacing("padding-left", horizontal)}
    ${globals.verticalSpacing("padding-right", horizontal)}
  `}
  ${mq.mediumAndBelow} {
    ${({ hideMobile }) => (hideMobile && "display: none")}
  }
  ${mq.largeAndUp} {
    ${({ hideDesktop }) => (hideDesktop && "display: none")}
  }
`

const IntroText = styled(Grid)`
  ${globals.verticalSpacing("margin-bottom", 0.5)}
`

const ImageWrapper = styled.div`
  ${mq.largerAndUp} {
    overflow: hidden;
    border-radius: var(--card-border-radius);
    box-shadow: ${shadow()};
  }
  ${mq.largeAndBelow} {
    .gatsby-image-wrapper,
    .video-wrapper {
      overflow: hidden;
      border-radius: var(--card-border-radius);
      box-shadow: ${shadow()};
    }
  }
  text-align: left;
  h3,
  p {
    transition: color ${animations.mediumSpeed} ease-in-out;
    margin: 0;
  }
  h3 {
    ${typography.articleTitleSmall}
    max-width: 24em;
    margin-bottom: 0.25em;
    margin-top: -0.1em;
    margin-top: 0.8em;
  }
  p {
    ${typography.bodySmall}
    max-width: 26em;
    margin-bottom: 0.1em;
  }
`

const ImageSlider = styled(Slider)`
  padding-bottom: 40px;
  ${mq.largerAndUp} {
    padding-bottom: 0;
    margin: -1px -1px;
    .slide-content {
      margin: -1px 0;
    }
  }
`

const NavCard = styled(Card)`
  cursor: pointer;
  text-align: left;
  width: 100%;
  margin-bottom: 6px;
  background: transparent;
  ${util.responsiveStyles("padding", 30, 20, 18, 18)}
  ${({ current }) =>
    current
      ? `
    background: ${colors.white};
    box-shadow: ${shadow()};
    h3 {
      color: ${colors.mainColor};
    }
    p {
      color: ${colors.textColor};
    }
  `
      : `
    background: transparent;
    box-shadow: none;
    &:hover {
      background: ${colors.hrColor};
    }
  `}
  h3, p {
    transition: color ${animations.mediumSpeed} ease-in-out;
    margin: 0;
  }
  h3 {
    ${typography.articleTitleSmall}
    max-width: 20em;
    margin-bottom: 0.25em;
    margin-top: -0.1em;
  }
  p {
    ${typography.bodySmall}
    max-width: 28em;
    margin-bottom: 0.1em;
  }
`

const SlideshowDots = styled.ul`
  list-style: none;
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: flex-start;
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 9999;
  margin: 0;
  max-width: 100%;
  padding: 0 var(--site-margins);
  ${mq.mediumAndUp} {
    padding: 0 ${100 / 14}vw;
  }
  > li {
    &:not(:last-of-type) {
      margin-right: 10px;
    }
  }
`

const SlideshowDot = styled.a`
  width: 10px;
  height: 10px;
  display: block;
  background: ${colors.lightTextColor};
  cursor: pointer;
  border-radius: 50%;
  opacity: 0.6;
  margin: 0;
  border: none;
  outline: none;
  transform: scale(0.6);
  transition: transform ${animations.mediumSpeed} ease-in-out,
    background ${animations.mediumSpeed} ease-in-out,
    opacity ${animations.mediumSpeed} ease-in-out;
  .slick-active & {
    transform: none;
    opacity: 1;
    background: ${colors.mainColor};
  }
`

const DesktopSlider = ({
  className,
  nextTheme,
  prevTheme,
  theme,
  spacing,
  isFirstSection,
  headline,
  introText,
  actions,
  slides,
  id,
  hideMobile,
  hideDesktop,
}) => {
  const slider = useRef(null)
  const [currentSlide, setCurrentSlide] = useState(0)

  const settings = {
    arrows: false,
    slidesToShow: 1,
    slidesToScroll: 1,
    vertical: true,
    swipe: true,
    speed: 500,
    beforeChange: (oldIndex, newIndex) => setCurrentSlide(newIndex),
    responsive: [
      {
        breakpoint: mq.largeBreakpoint,
        settings: {
          vertical: false,
          dots: true,
          appendDots: dots => <SlideshowDots>{dots}</SlideshowDots>,
          customPaging: (slider, i) => (
            <SlideshowDot active={slider === i}>
              {i}
              {slider.current}
            </SlideshowDot>
          ),
        },
      },
    ],
  }

  return (
    <Wrapper
      className={className}
      prevTheme={prevTheme}
      setTheme={theme}
      nextTheme={nextTheme}
      isFirstSection={isFirstSection}
      id={id}
      vertical={spacing?.vertical}
      horizontal={spacing?.horizontal}
      hideMobile={hideMobile}
      hideDesktop={hideDesktop}
    >
      {introText && (
        <IntroText small="container" medium="1 [12] 1">
          <TextLockup
            eyebrow={introText.eyebrow}
            text={introText._rawText}
            newStyle={introText?.newStyle}
            alignment="center"
            actions={actions}
          />
        </IntroText>
      )}
      <Grid
        small="[1]"
        large="2 [13] 1 [10] 2"
        larger="2 [15] 1 [8] 2"
        vAlign="top"
      >
        <ImageWrapper>
          <ImageSlider ref={slider} {...settings}>
            {slides &&
              slides.map((item, index) => {
                return (
                  <div className="slide-content" key={item._key}>
                    <Grid small="container" medium="1 [12] 1" large="[1]">
                      <div>
                        <Media
                          media={item.media}
                          ratio={9 / 16}
                          altText={item.title}
                          rounded
                        />
                        <ResponsiveComponent
                          small={
                            <div>
                              <h3>{item.title}</h3>
                              <p>{item.text}</p>
                            </div>
                          }
                          large={<span />}
                        />
                      </div>
                    </Grid>
                  </div>
                )
              })}
          </ImageSlider>
        </ImageWrapper>
        <div>
          <ResponsiveComponent
            small={<span />}
            large={
              slides &&
              slides.map((item, index) => {
                return (
                  <NavCard
                    as="button"
                    current={currentSlide === index}
                    onClick={() => slider?.current?.slickGoTo(index)}
                    key={item._key + "-nav"}
                  >
                    <h3>{item.title}</h3>
                    <p>{item.text}</p>
                  </NavCard>
                )
              })
            }
          />
        </div>
      </Grid>
    </Wrapper>
  )
}

export default DesktopSlider
